import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Lightbulb } from "lucide-react";
import { toast } from "sonner";

const durumRenk = { Beklemede: "bg-gold/15 text-[#9a7d10]", Onaylandı: "bg-sage/15 text-sage", Reddedildi: "bg-destructive/10 text-destructive" };
const EMPTY = { kitap_adi: "", yazar: "", yayinevi: "", kunye_bilgisi: "", ozet_abstract: "", etiketler: "", zorluk_seviyesi: "Orta", kapak_resmi: "", not_: "" };

export default function Suggestions() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/suggestions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const etiketler = form.etiketler.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post("/suggestions", { ...form, etiketler });
      setForm(EMPTY);
      toast.success("Önerin gönderildi! Yöneticiler değerlendirecek.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="fade-up">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra flex items-center gap-2"><Lightbulb size={14} /> Katkı</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Kitap Önerilerim</h1>
        <p className="text-ink-2 mt-2">Kütüphaneye eklenmesini istediğin kitabın tüm bilgilerini doldur — onaylanınca aynı bilgilerle eklenir.</p>
      </div>

      <form onSubmit={submit} className="border border-line rounded-xl p-6 bg-surface mt-8 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Kitap Adı *</Label><Input data-testid="sug-title" required value={form.kitap_adi} onChange={(e) => setForm({ ...form, kitap_adi: e.target.value })} className="mt-1" /></div>
          <div><Label>Yazar *</Label><Input data-testid="sug-author" required value={form.yazar} onChange={(e) => setForm({ ...form, yazar: e.target.value })} className="mt-1" /></div>
          <div><Label>Yayınevi</Label><Input data-testid="sug-publisher" value={form.yayinevi} onChange={(e) => setForm({ ...form, yayinevi: e.target.value })} className="mt-1" /></div>
          <div>
            <Label>Zorluk Seviyesi</Label>
            <select data-testid="sug-difficulty" value={form.zorluk_seviyesi} onChange={(e) => setForm({ ...form, zorluk_seviyesi: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-line bg-surface px-3 text-sm">
              <option value="Başlangıç">Başlangıç</option><option value="Orta">Orta</option><option value="İleri">İleri</option>
            </select>
          </div>
        </div>
        <div><Label>Künye Bilgisi</Label><Input data-testid="sug-imprint" value={form.kunye_bilgisi} onChange={(e) => setForm({ ...form, kunye_bilgisi: e.target.value })} className="mt-1" placeholder="Baskı, ISBN vb." /></div>
        <div><Label>Etiketler (virgülle)</Label><Input data-testid="sug-tags" value={form.etiketler} onChange={(e) => setForm({ ...form, etiketler: e.target.value })} className="mt-1" placeholder="Stoacılık, Etik, Antik Felsefe" /></div>
        <div><Label>Kapak Görseli URL</Label><Input data-testid="sug-cover" value={form.kapak_resmi} onChange={(e) => setForm({ ...form, kapak_resmi: e.target.value })} className="mt-1" placeholder="https://..." /></div>
        <div><Label>Özet / Abstract</Label><Textarea data-testid="sug-abstract" value={form.ozet_abstract} onChange={(e) => setForm({ ...form, ozet_abstract: e.target.value })} className="mt-1" rows={3} /></div>
        <div><Label>Neden önerdin? (opsiyonel)</Label><Textarea data-testid="sug-note" value={form.not_} onChange={(e) => setForm({ ...form, not_: e.target.value })} className="mt-1" rows={2} /></div>
        <Button type="submit" disabled={loading} data-testid="sug-submit" className="bg-terra hover:bg-terra-hover text-white rounded-full px-6">{loading ? "..." : "Öneri Gönder"}</Button>
      </form>

      <div className="mt-8 space-y-3">
        {list.map((s) => (
          <div key={s.id} className="border border-line rounded-lg p-4 bg-surface flex items-center justify-between" data-testid={`sug-${s.id}`}>
            <div>
              <p className="font-medium text-ink">{s.kitap_adi}</p>
              <p className="text-sm text-muted-foreground">{s.yazar}{s.yayinevi ? ` · ${s.yayinevi}` : ""}</p>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full ${durumRenk[s.durum]}`}>{s.durum}</span>
          </div>
        ))}
        {list.length === 0 && <p className="text-muted-foreground text-sm">Henüz öneride bulunmadın.</p>}
      </div>
    </div>
  );
}
